import { type CrossClientQueryExecution } from '../../../../models/CrossClient'
import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { useAppSelector } from '../../../../store/hooks'
import { selectCrossClientLandingTeammates, selectCrossClientLandingTeammatesLoading } from '../../../../store/crossClient/crossClientLanding'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { formatDate, getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import classNames from 'classnames'
import { UserAvatar } from '../../../../components/UserAvatar/UserAvatar'
import { getInitials } from '../../../utils'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { Button } from '../../../../components/Button/Button'
import { CrossClientResultSourceIcon } from '../../Results/Sources/CrossClientResultSourceIcon'
import { sortSources } from '../../Results/Sources/utils'
import { ReactComponent as ZocksIcon } from '../../../../assets/images/ZocksIcon-primary.svg'
import { CrossClientQueuedProgress } from '../../Progress/CrossClientQueuedProgress'

interface CrossClientQueuedElementProps {
  execution: CrossClientQueryExecution
  onDelete: (execution: CrossClientQueryExecution) => void
}

export const CrossClientQueuedElement: React.FC<CrossClientQueuedElementProps> = ({ execution, onDelete }) => {
  const navigate = useNavigate()

  const { tracked } = useTracking()

  const teammates = useAppSelector(selectCrossClientLandingTeammates)
  const teammatesLoading = useAppSelector(selectCrossClientLandingTeammatesLoading)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)

  const [hovered, setHovered] = useState<boolean>(false)

  const userName = useMemo(() => {
    return execution.createdBy?.id === currentUser.id
      ? getUserDisplayName(currentUser)
      : getUserDisplayName(teammates.find((t) => t.id === execution.createdBy?.id))
  }, [currentUser, execution.createdBy?.id, teammates])

  const handleExecutionClicked = tracked(() => navigate(`/cross-client/executions/${execution.id}`), {
    eventName: TrackEvent.X_CLIENT_HISTORY_OPENED,
    eventDetails: {
      executionId: execution.id,
    },
  })

  const handleDeleteExecution = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete(execution)
  }

  return (
    <div
      className={classNames(
        'd-flex gap-3 p-3 align-items-center border border-1  zocks-border-0.75 bg-white w-100 cross-client-history-element cross-client-transition',
        {
          'zocks-border-color-primary cross-client-history-element-box-shadow': hovered,
          'zocks-border-color-gray-300': !hovered,
        },
      )}
      role='button'
      onClick={handleExecutionClicked}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <CrossClientQueuedProgress className='cross-client-landing-progress-container' iconClassName='fs-6' />
      <div className='d-flex zocks-gap-2 justify-content-between align-items-center w-100'>
        <div className='d-flex flex-column gap-1 flex-grow-1 w-100'>
          <div className='paragraph-medium medium text-truncate cross-client-history-question'>{execution.userQuestion}</div>
          <div className='d-flex gap-3'>
            <div className='d-flex gap-2 align-items-center'>
              <ZocksIcon width='1rem' height='1rem' />
              {sortSources(execution.sources ?? []).map((source, idx) => (
                <CrossClientResultSourceIcon key={idx} source={source} size='md' />
              ))}
            </div>
            <div className='d-flex gap-1 align-items-center'>
              {teammatesLoading && !userName ? (
                <div className='skeleton' style={{ width: '6rem', height: '1rem' }} />
              ) : (
                <>
                  <UserAvatar
                    width='1rem'
                    height='1rem'
                    fontSize='0.5rem'
                    className='lead-extra-small zocks-text-white zocks-bg-color-gray-900'
                    useInitials
                    initialLetters={getInitials(userName.split(' ')[0])}
                  />
                  <div className='paragraph-extra-small'>{userName}</div>
                </>
              )}
            </div>
            <div className='d-flex gap-1 align-items-center'>
              <i className='bi bi-calendar' />
              <div className='paragraph-extra-small'>{formatDate(execution.createdAt)}</div>
            </div>
          </div>
        </div>
        <div className='d-flex gap-3 align-items-center'>
          <Tooltip className='zocks-text-gray-500' placement='top' title='Delete query'>
            <Button variant='header' className='px-2 zocks-border-0.5' onClick={handleDeleteExecution}>
              <i className='bi bi-trash' />
            </Button>
          </Tooltip>
          <i className={classNames('bi bi-arrow-right fs-5', { 'zocks-text-primary': hovered })} />
        </div>
      </div>
    </div>
  )
}
